import type {
  DocumentTsaTag,
  UploadBatchDeclaration,
  UploadBatchEntry,
  UploadBatchInput,
  UploadBatchReceipt,
  UploadInputDeclaration,
} from '@contract-analyzer/api-client';
import type { Uppy, UppyEventMap } from '@uppy/core';

import { apiClient } from '@/lib/api';
import type { DocumentUploadPlugin, TransferError, UploadBody, UploadMeta } from '@/lib/document-upload-plugin';
import { getUploadErrorMessage, isArchiveFile, isRetryableRequestError, MAX_UPLOAD_BATCH_INPUTS } from '@/lib/document-upload';
import { selectedFolderPath, type SelectedUpload } from '@/lib/upload-selection';
import { deriveInputStatus, type SettledTransfer, type TransferProgress, type UploadInputStatus } from '@/lib/upload-status';

export interface UploadOptions {
  readonly tags?: readonly string[];
  readonly folderPath?: string | null;
  readonly tsaEnabled?: boolean;
  readonly tsaTags?: readonly DocumentTsaTag[];
}
export type UploadPreferences = Pick<UploadOptions, 'tags' | 'folderPath'>;

/** One selected input with its derived status and, once received, its document entries. */
export interface UploadItem {
  readonly inputId: string;
  readonly filename: string;
  readonly path: string;
  readonly sizeBytes: number;
  readonly isArchive: boolean;
  readonly status: UploadInputStatus;
  readonly entries: readonly UploadBatchEntry[];
}

interface UploadBatchSummary {
  /** Inputs the server holds: verifying, received or rejected after receipt. */
  readonly received: number;
  readonly sending: number;
  readonly retryable: number;
  readonly needsAttention: number;
  readonly bytesTotal: number;
  readonly bytesSent: number;
}

/**
 * One upload as the user selected it. A selection larger than one server batch
 * holds is declared as consecutive batches; its view combines them, and
 * `batchId` is the first one's ID. Every store action accepts it.
 */
export interface UploadBatchView {
  readonly batchId: string;
  readonly projectId: string;
  readonly createdAt: string;
  readonly inputCount: number;
  readonly tsaEnabled: boolean;
  readonly items: readonly UploadItem[];
  readonly summary: UploadBatchSummary;
  readonly isSending: boolean;
  readonly loading: boolean;
  readonly error: string | null;
}

export interface ProjectUploadsView {
  /** Newest first. */
  readonly batches: readonly UploadBatchView[];
  readonly selectedBatchId: string | null;
  readonly hasOlder: boolean;
  readonly historyError: string | null;
  readonly preferences: UploadPreferences;
}

export interface UploadActivity {
  readonly sending: boolean;
  /** The batch the header indicator links to: one still sending, else the newest started here. */
  readonly current: Pick<UploadBatchView, 'batchId' | 'projectId' | 'isSending'> | null;
}

/** A transfer of a batch started in this tab that failed after its automatic retries. */
interface TransferFailed {
  /** The upload the transfer belongs to, as `UploadBatchView.batchId` names it. */
  readonly batchId: string;
  /** What ended the transfer, such as the API refusing its upload session. */
  readonly failure: unknown;
}

/** Documents a batch started in this tab newly admitted to its project. */
export interface DocumentsAdmitted {
  readonly projectId: string;
  readonly batchId: string;
  readonly documentIds: readonly string[];
  readonly tsaEnabled: boolean;
}

type Transfers = Uppy<UploadMeta, UploadBody>;
interface TransferRuntime {
  readonly uppy: Transfers;
  readonly plugin: DocumentUploadPlugin;
  readonly TransferError: typeof TransferError;
}

/** What every input of one selection shares. */
interface UploadMetaSettings {
  readonly projectId: string;
  readonly destination: string | null;
  readonly tags: readonly string[];
  readonly tsaEnabled: boolean;
  readonly tsaTags: readonly DocumentTsaTag[];
}

interface LocalSource {
  readonly file: File;
  readonly meta: UploadMeta;
}

interface Settlement {
  readonly settled: SettledTransfer;
  /** Receipt reads started after this sequence number supersede the settlement. */
  readonly sequence: number;
}

interface CachedItem {
  readonly transfer: TransferProgress | undefined;
  readonly settlement: Settlement | undefined;
  readonly receipt: UploadBatchInput | undefined;
  readonly resendable: boolean;
  readonly resent: boolean;
  readonly item: UploadItem;
}

/** What the store reads from a declaration, whether declared here or read back in a receipt. */
type DeclaredBatch = Pick<UploadBatchReceipt['declaration'], 'inputs' | 'tsa_enabled'>;

/** Entries of an archive the receipt omitted for size, read once for the session that settled it. */
interface OmittedEntries {
  readonly sessionId: string;
  readonly entries: UploadBatchInput['entries'];
}

interface BatchRecord {
  readonly batchId: string;
  /** The upload this batch belongs to: the first batch of its selection. */
  readonly uploadId: string;
  readonly projectId: string;
  readonly createdAt: string;
  readonly inputCount: number;
  /** Started in this tab: its files are in memory and its admissions are announced. */
  readonly live: boolean;
  declaring: boolean;
  /** A declaration the server has not accepted yet; the first retry sends it again. */
  undeclared: UploadBatchDeclaration | null;
  declaration: DeclaredBatch | null;
  receipt: ReadonlyMap<string, UploadBatchInput> | null;
  /** Archives too large for the aggregate receipt; every other input's entries come with it. */
  readonly omitted: Map<string, OmittedEntries>;
  readingOmitted: boolean;
  readonly settlements: Map<string, Settlement>;
  readonly sources: Map<string, LocalSource>;
  /** What every input of a selection declared in this tab shares; a restored batch has none. */
  readonly settings: UploadMetaSettings | null;
  /** Archives sent again as a new upload to recover entries that failed. */
  readonly resent: Set<string>;
  readonly announced: Set<string>;
  loading: boolean;
  error: string | null;
  reading: Promise<void> | null;
  pollAgain: boolean;
  pollDelay: number;
  pollTimer: ReturnType<typeof setTimeout> | undefined;
  readonly items: Map<string, CachedItem>;
  view: UploadBatchView;
}

interface ProjectRecord {
  loaded: boolean;
  cursor: string | null;
  historyError: string | null;
  selected: string | null;
  preferences: UploadPreferences | null;
  view: ProjectUploadsView;
}

const EMPTY_SUMMARY: UploadBatchSummary = { received: 0, sending: 0, retryable: 0, needsAttention: 0, bytesTotal: 0, bytesSent: 0 };
const EMPTY_PROJECT: ProjectUploadsView = { batches: [], selectedBatchId: null, hasOlder: false, historyError: null, preferences: {} };
const EMPTY_ACTIVITY: UploadActivity = { sending: false, current: null };
const PUBLISH_INTERVAL_MS = 150;
const POLL_INITIAL_MS = 2_000;
const POLL_SOON_MS = 1_000;
const POLL_MAX_MS = 15_000;
const VERIFYING = new Set<UploadBatchInput['session_status']>(['ready', 'processing', 'stalled']);
const UNREACHABLE = 'Couldn’t reach the server. Check your connection, then retry.';

/** The uploader's code could not be fetched; like a failed request, loading it again can succeed. */
class TransferRuntimeUnavailable extends Error {
  constructor(cause: unknown) {
    super(UNREACHABLE, { cause });
    this.name = 'TransferRuntimeUnavailable';
  }
}

/** A failure the same files can overcome by being sent again, as opposed to a refusal. */
function isInterruption(error: Error): boolean {
  return error instanceof TransferRuntimeUnavailable || isRetryableRequestError(error);
}

function inChunks<Item>(items: readonly Item[], size: number): Item[][] {
  return Array.from({ length: Math.ceil(items.length / size) }, (_, index) => items.slice(index * size, (index + 1) * size));
}

/** The combined view of an upload declared as several batches, `first` among them. */
function combineViews(first: UploadBatchView, parts: readonly UploadBatchView[]): UploadBatchView {
  const summary = parts.reduce((total, { summary: part }) => ({
    received: total.received + part.received,
    sending: total.sending + part.sending,
    retryable: total.retryable + part.retryable,
    needsAttention: total.needsAttention + part.needsAttention,
    bytesTotal: total.bytesTotal + part.bytesTotal,
    bytesSent: total.bytesSent + part.bytesSent,
  }), EMPTY_SUMMARY);
  return {
    ...first,
    inputCount: parts.reduce((total, part) => total + part.inputCount, 0),
    items: parts.flatMap(part => part.items), summary,
    isSending: parts.some(part => part.isSending), loading: parts.some(part => part.loading),
    error: parts.find(part => part.error !== null)?.error ?? null,
  };
}

async function loadTransferRuntime(): Promise<TransferRuntime> {
  // Uppy and the Azure SDK load with the first upload, not with the application shell.
  const [{ Uppy: UppyCore }, transport] = await Promise.all([import('@uppy/core'), import('@/lib/document-upload-plugin')]);
  const uppy = new UppyCore<UploadMeta, UploadBody>({
    autoProceed: false,
    // Uppy derives file IDs from a lowercased name and path, so two inputs that
    // differ only in letter case, or the same file selected into two batches,
    // would share an ID and one would be refused. Every declared input already
    // has a unique identity; Uppy files take it.
    onBeforeFileAdded: (file, files) => (Object.hasOwn(files, file.meta.inputId) ? false : { ...file, id: file.meta.inputId }),
  });
  uppy.use(transport.DocumentUploadPlugin);
  const plugin = uppy.getPlugin<DocumentUploadPlugin>('DocumentUpload');
  if (plugin === undefined) throw new Error('The upload transport did not install.');
  return { uppy, plugin, TransferError: transport.TransferError };
}

function declaredInputs(record: BatchRecord): readonly UploadInputDeclaration[] {
  if (record.declaration !== null) return record.declaration.inputs;
  return record.receipt === null ? [] : [...record.receipt.values()].map(input => input.declaration);
}

function withOmittedEntries(record: BatchRecord, input: UploadBatchInput): UploadBatchInput {
  if (!input.entries_omitted) return input;
  const read = record.omitted.get(input.declaration.input_id);
  return read !== undefined && read.sessionId === input.session_id ? { ...input, entries: read.entries } : input;
}

/** The next settled archive whose omitted entries were not read for its current session. */
function nextOmittedRead(record: BatchRecord): { readonly inputId: string; readonly sessionId: string } | undefined {
  for (const input of record.receipt?.values() ?? []) {
    const sessionId = input.session_id;
    if (!input.entries_omitted || !input.received || sessionId == null) continue;
    if (record.omitted.get(input.declaration.input_id)?.sessionId !== sessionId) return { inputId: input.declaration.input_id, sessionId };
  }
  return undefined;
}

/** Retryable inputs of the batch view the caller asked for, of the kind `accept` selects. */
function retryableInputs(record: BatchRecord, wanted: ReadonlySet<string> | null, accept: (item: UploadItem) => boolean): string[] {
  return record.view.items.flatMap(item =>
    item.status.retryable && accept(item) && (wanted === null || wanted.has(item.inputId)) ? [item.inputId] : []);
}

function isVerifying(record: BatchRecord, inputId: string): boolean {
  const settled = record.settlements.get(inputId)?.settled;
  if (settled?.kind === 'sent') return VERIFYING.has(settled.session.status);
  return VERIFYING.has(record.receipt?.get(inputId)?.session_status ?? null);
}

/** Files stay in memory only; authoritative receipts are read back from the server. */
export class UploadBatchStore {
  readonly #records = new Map<string, BatchRecord>();
  /** Every upload's batches in declaration order, by upload ID. */
  readonly #uploads = new Map<string, BatchRecord[]>();
  readonly #combined = new Map<string, { readonly parts: readonly UploadBatchView[]; readonly view: UploadBatchView }>();
  readonly #projects = new Map<string, ProjectRecord>();
  readonly #listeners = new Set<() => void>();
  readonly #admissionListeners = new Set<(event: DocumentsAdmitted) => void>();
  readonly #failureListeners = new Set<(event: TransferFailed) => void>();
  readonly #dirtyBatches = new Set<string>();
  readonly #dirtyProjects = new Set<string>();
  #transfers: ReadonlyMap<string, TransferProgress> = new Map();
  #runtime: Promise<TransferRuntime> | null = null;
  #loaded: TransferRuntime | null = null;
  #activity = EMPTY_ACTIVITY;
  #publishTimer: ReturnType<typeof setTimeout> | undefined;
  #sequence = 0;
  #owners = 0;
  #disposed = false;

  /**
   * Hold the store for a mounted owner. The last release disposes it after a
   * microtask, so a remounting owner (React StrictMode) keeps the same store.
   */
  retain = (): (() => void) => {
    if (this.#owners === 0) document.addEventListener('visibilitychange', this.#onVisibility);
    this.#owners += 1;
    return () => {
      this.#owners -= 1;
      queueMicrotask(() => { if (this.#owners === 0) this.#dispose(); });
    };
  };

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => { this.#listeners.delete(listener); };
  };

  onDocumentsAdmitted(listener: (event: DocumentsAdmitted) => void): () => void {
    this.#admissionListeners.add(listener);
    return () => { this.#admissionListeners.delete(listener); };
  }

  /**
   * Run a callback for each transfer that failed after its automatic retries,
   * such as a refused upload session. The batch view reports the failure either
   * way; this lets a surface that outlives the upload react to why.
   */
  onTransferFailed(listener: (event: TransferFailed) => void): () => void {
    this.#failureListeners.add(listener);
    return () => { this.#failureListeners.delete(listener); };
  }

  getProject = (projectId: string): ProjectUploadsView => this.#projects.get(projectId)?.view ?? EMPTY_PROJECT;
  getActivity = (): UploadActivity => this.#activity;

  /**
   * Merge one edit into the project's upload preferences. Views publish on a
   * short timer, so a caller spreading its last view would drop an edit made
   * just before (tags, then the folder) from the declared batch.
   */
  updatePreferences(projectId: string, change: UploadPreferences): void {
    const project = this.#project(projectId);
    project.preferences = { ...project.preferences, ...change };
    this.#touchProject(projectId);
  }

  /** The latest preferences, including edits the published view does not show yet. */
  getPreferences(projectId: string): UploadPreferences {
    return this.#projects.get(projectId)?.preferences ?? {};
  }

  /**
   * Load the transport once files are chosen, so a connection lost before
   * Upload is pressed interrupts requests rather than the uploader itself.
   */
  prepareTransfers(): void {
    this.#ensureRuntime().catch(() => undefined);
  }

  select(projectId: string, uploadId: string): void {
    this.#project(projectId).selected = uploadId;
    this.#touchProject(projectId);
    for (const record of this.#uploads.get(uploadId) ?? []) if (record.receipt === null) void this.#poll(record);
  }

  /**
   * Declare the selection as durable batches of at most
   * `MAX_UPLOAD_BATCH_INPUTS` inputs, then hand their files to the transfer
   * queue. Resolves with the upload's ID once transfers are scheduled;
   * completion is observed through the upload's view and `onDocumentsAdmitted`.
   *
   * A batch the server could not be reached to declare keeps its files as
   * interrupted inputs: retrying any of them declares the batch first. A
   * refusal of the whole selection rejects instead.
   */
  async start(projectId: string, selection: readonly SelectedUpload[], options: UploadOptions = {}): Promise<string> {
    const runtime = this.#ensureRuntime();
    const createdAt = new Date().toISOString();
    const settings: UploadMetaSettings = {
      projectId,
      destination: options.folderPath?.trim() || null,
      tags: [...(options.tags ?? [])],
      tsaEnabled: options.tsaEnabled ?? false,
      tsaTags: [...(options.tsaTags ?? [])],
    };
    const parts = inChunks(selection, MAX_UPLOAD_BATCH_INPUTS);
    const uploadId = crypto.randomUUID();
    const records = parts.map((part, index) => this.#declareLocally(index === 0 ? uploadId : crypto.randomUUID(), uploadId, part, settings, createdAt));
    this.#uploads.set(uploadId, records);
    this.#project(projectId).selected = uploadId;
    const failures = await Promise.all(records.map(record => this.#declare(record, runtime)));
    const refusal = failures.find((failure): failure is Error => failure !== null && !isInterruption(failure));
    if (refusal !== undefined && records.every(record => record.undeclared !== null)) {
      for (const record of records) this.#records.delete(record.batchId);
      this.#uploads.delete(uploadId);
      this.#combined.delete(uploadId);
      this.#touchProject(projectId);
      throw refusal;
    }
    records.forEach((record, index) => {
      const failure = failures[index] ?? null;
      const ids = [...record.sources.keys()];
      if (failure === null) this.#send(record, ids);
      else this.#settleUnsent(record, ids, failure);
    });
    return uploadId;
  }

  /**
   * Re-read each batch's receipt, then resend each input that is still retryable.
   * A received archive with recoverable failed entries is sent again as a new
   * upload instead: its own session succeeded and cannot be replaced in its batch.
   */
  async retry(uploadId: string, inputIds?: readonly string[]): Promise<void> {
    const wanted = inputIds === undefined ? null : new Set(inputIds);
    const records = this.#uploads.get(uploadId) ?? [];
    await Promise.all([
      ...records.map(record => this.#retryBatch(record, wanted)),
      ...records.map(record => this.#resendArchives(record, wanted)),
    ]);
  }

  cancel(uploadId: string): void {
    const loaded = this.#loaded;
    if (loaded === null) return;
    for (const record of this.#uploads.get(uploadId) ?? []) {
      const files = loaded.uppy.getFiles().filter(file => file.meta.batchId === record.batchId);
      for (const file of files) record.settlements.set(file.meta.inputId, this.#settle({ kind: 'cancelled' }));
      loaded.uppy.removeFiles(files.map(file => file.id));
    }
  }

  /** Cancel every transfer and wait until the server has released their reservations. */
  async cancelActive(): Promise<void> {
    for (const [uploadId, records] of this.#uploads) if (records.some(record => record.view.isSending)) this.cancel(uploadId);
    await this.#loaded?.plugin.settleCancellations();
  }

  refresh = (uploadId: string): void => {
    for (const record of this.#uploads.get(uploadId) ?? []) void this.#poll(record);
  };

  restore = async (projectId: string): Promise<void> => {
    const project = this.#project(projectId);
    if (project.loaded) return;
    project.loaded = true;
    await this.#loadPage(projectId);
  };

  loadOlder = async (projectId: string): Promise<void> => {
    const cursor = this.#projects.get(projectId)?.cursor;
    if (cursor != null) await this.#loadPage(projectId, cursor);
  };

  #dispose(): void {
    this.#disposed = true;
    clearTimeout(this.#publishTimer);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    for (const record of this.#records.values()) clearTimeout(record.pollTimer);
    this.#destroyTransfers();
    this.#listeners.clear();
    this.#admissionListeners.clear();
    this.#failureListeners.clear();
  }

  #ensureRuntime(): Promise<TransferRuntime> {
    this.#runtime ??= (async () => {
      let runtime: TransferRuntime;
      try {
        runtime = await loadTransferRuntime();
      } catch (error) {
        // A chunk that failed to load (usually offline) loads again on the next attempt.
        this.#runtime = null;
        throw new TransferRuntimeUnavailable(error);
      }
      this.#loaded = runtime;
      this.#listen(runtime);
      if (this.#disposed) this.#destroyTransfers();
      return runtime;
    })();
    return this.#runtime;
  }

  /** Record one batch of a selection, with its declaration and in-memory files, before sending anything. */
  #declareLocally(
    batchId: string, uploadId: string, selection: readonly SelectedUpload[], settings: UploadMetaSettings, createdAt: string,
  ): BatchRecord {
    const { projectId, destination, tags, tsaEnabled, tsaTags } = settings;
    const inputs = selection.map(({ file, relativePath }) => {
      const inputId = crypto.randomUUID();
      const declared: UploadInputDeclaration = {
        input_id: inputId, filename: file.name, relative_path: relativePath, size_bytes: file.size, content_type: file.type || null,
      };
      const meta: UploadMeta = {
        projectId, batchId, inputId, relativePath, folderPath: selectedFolderPath(relativePath, destination), tags, tsaEnabled, tsaTags,
      };
      return { declared, source: { file, meta } };
    });
    const declaration: UploadBatchDeclaration = {
      batch_id: batchId, inputs: inputs.map(input => input.declared), tags: [...tags], folder_path: destination, tsa_enabled: tsaEnabled, tsa_tags: [...tsaTags],
    };
    const record = this.#addRecord({ batchId, uploadId, projectId, createdAt, inputCount: selection.length, live: true, declaration, settings });
    record.undeclared = declaration;
    for (const { declared, source } of inputs) record.sources.set(declared.input_id, source);
    this.#touch(record);
    return record;
  }

  /** Declare the batch (the server accepts the same declaration again) and load the transport; the first failure, if any. */
  async #declare(record: BatchRecord, runtime: Promise<TransferRuntime>): Promise<Error | null> {
    let failure: Error | null = null;
    const declaration = record.undeclared;
    if (declaration !== null) {
      try {
        await apiClient.createUploadBatch(record.projectId, declaration);
        record.undeclared = null;
      } catch (error) {
        failure = error instanceof Error ? error : new Error(getUploadErrorMessage(error));
      }
    }
    try {
      await runtime;
    } catch (error) {
      failure ??= error instanceof Error ? error : new Error(getUploadErrorMessage(error));
    }
    record.declaring = false;
    this.#touch(record);
    return failure;
  }

  /** Inputs a failure kept from being sent: interrupted when sending them again can succeed. */
  #settleUnsent(record: BatchRecord, inputIds: readonly string[], failure: Error): void {
    const settled: SettledTransfer = isInterruption(failure)
      ? { kind: 'failed', retryable: true, message: UNREACHABLE }
      : { kind: 'failed', retryable: false, message: getUploadErrorMessage(failure) };
    for (const id of inputIds) record.settlements.set(id, this.#settle(settled));
    this.#touch(record);
  }

  async #retryBatch(record: BatchRecord, wanted: ReadonlySet<string> | null): Promise<void> {
    const requested = () => {
      this.#rebuild(record);
      return retryableInputs(record, wanted, item => item.status.phase !== 'received');
    };
    if (requested().length === 0) return;
    const failure = await this.#declare(record, this.#ensureRuntime());
    if (failure !== null) {
      this.#settleUnsent(record, requested(), failure);
      return;
    }
    await this.#poll(record);
    const ids = requested();
    for (const id of ids) record.settlements.delete(id);
    this.#send(record, ids);
  }

  /** Send received archives with recoverable failed entries again, together, as one new upload. */
  async #resendArchives(record: BatchRecord, wanted: ReadonlySet<string> | null): Promise<void> {
    this.#rebuild(record);
    const { settings } = record;
    const ids = retryableInputs(record, wanted, item => item.status.phase === 'received');
    if (settings === null || ids.length === 0) return;
    const selection = ids.flatMap(id => {
      const source = record.sources.get(id);
      return source === undefined ? [] : [{ file: source.file, relativePath: source.meta.relativePath }];
    });
    for (const id of ids) record.resent.add(id);
    this.#touch(record);
    try {
      await this.start(settings.projectId, selection, {
        tags: settings.tags, folderPath: settings.destination, tsaEnabled: settings.tsaEnabled, tsaTags: settings.tsaTags,
      });
    } catch (error) {
      for (const id of ids) record.resent.delete(id);
      record.error = getUploadErrorMessage(error);
      this.#touch(record);
    }
  }

  /**
   * Stop local work only. Uninstalling the transport first keeps Uppy's
   * teardown from cancelling sessions: after a workspace switch or sign-out the
   * API session no longer belongs to the tenant that owns them. Explicit
   * cancellation runs before a session change instead (see cancelActive).
   */
  #destroyTransfers(): void {
    const loaded = this.#loaded;
    if (loaded === null) return;
    loaded.uppy.removePlugin(loaded.plugin);
    loaded.uppy.destroy();
  }

  #listen({ uppy, TransferError: Failure }: TransferRuntime): void {
    uppy.on('state-update', this.#onTransferState);
    uppy.on('upload-success', (file, response) => {
      if (file === undefined || response.body === undefined) return;
      this.#settleFile(file.id, file.meta, { kind: 'sent', session: response.body });
    });
    uppy.on('upload-error', (file, error) => {
      if (file === undefined) return;
      const record = this.#records.get(file.meta.batchId);
      const transferFailure = error instanceof Failure;
      this.#settleFile(file.id, file.meta, {
        kind: 'failed', message: error.message, retryable: transferFailure && error.retryable,
      });
      if (record === undefined) return;
      const event: TransferFailed = { batchId: record.uploadId, failure: transferFailure ? error.cause : error };
      for (const listener of this.#failureListeners) listener(event);
    });
  }

  #onTransferState: UppyEventMap<UploadMeta, UploadBody>['state-update'] = (previous, next) => {
    if (previous.files === next.files) return;
    const transfers = new Map<string, TransferProgress>();
    for (const file of Object.values(next.files)) {
      const earlier = this.#transfers.get(file.meta.inputId);
      const progress: TransferProgress = { started: file.progress.uploadStarted != null, percentage: file.progress.percentage ?? 0 };
      const unchanged = earlier?.started === progress.started && earlier.percentage === progress.percentage;
      transfers.set(file.meta.inputId, unchanged ? earlier : progress);
      if (!unchanged) this.#dirtyBatches.add(file.meta.batchId);
    }
    for (const file of Object.values(previous.files)) {
      if (!transfers.has(file.meta.inputId)) this.#dirtyBatches.add(file.meta.batchId);
    }
    this.#transfers = transfers;
    this.#schedulePublish();
  };

  /** A settled file leaves Uppy; its outcome lives on in the batch until a newer receipt supersedes it. */
  #settleFile(fileId: string, meta: UploadMeta, settled: SettledTransfer): void {
    const record = this.#records.get(meta.batchId);
    record?.settlements.set(meta.inputId, this.#settle(settled));
    this.#loaded?.uppy.removeFile(fileId);
    if (record !== undefined) this.#pollSoon(record);
  }

  #settle(settled: SettledTransfer): Settlement {
    this.#sequence += 1;
    return { settled, sequence: this.#sequence };
  }

  #send(record: BatchRecord, inputIds: readonly string[]): void {
    const uppy = this.#loaded?.uppy;
    if (uppy === undefined || inputIds.length === 0 || this.#disposed) return;
    uppy.addFiles(inputIds.flatMap(id => {
      const source = record.sources.get(id);
      return source === undefined ? [] : [{ name: source.file.name, type: source.file.type, data: source.file, meta: source.meta, source: 'Local' }];
    }));
    this.#touch(record);
    uppy.upload().catch(() => undefined);
  }

  #project(projectId: string): ProjectRecord {
    let project = this.#projects.get(projectId);
    if (project === undefined) {
      project = { loaded: false, cursor: null, historyError: null, selected: null, preferences: null, view: EMPTY_PROJECT };
      this.#projects.set(projectId, project);
    }
    return project;
  }

  #addRecord(init: Pick<BatchRecord, 'batchId' | 'uploadId' | 'projectId' | 'createdAt' | 'inputCount' | 'live' | 'declaration' | 'settings'>): BatchRecord {
    const record: BatchRecord = {
      ...init, declaring: init.live, undeclared: null, receipt: null, omitted: new Map(), readingOmitted: false,
      settlements: new Map(), sources: new Map(), resent: new Set(), announced: new Set(),
      loading: !init.live, error: null, reading: null, pollAgain: false, pollDelay: POLL_INITIAL_MS, pollTimer: undefined,
      items: new Map(),
      view: {
        batchId: init.batchId, projectId: init.projectId, createdAt: init.createdAt, inputCount: init.inputCount,
        tsaEnabled: init.declaration?.tsa_enabled ?? false, items: [], summary: EMPTY_SUMMARY, isSending: init.live,
        loading: !init.live, error: null,
      },
    };
    this.#records.set(record.batchId, record);
    return record;
  }

  async #loadPage(projectId: string, cursor?: string): Promise<void> {
    const project = this.#project(projectId);
    try {
      const page = await apiClient.listUploadBatches(projectId, cursor);
      if (this.#disposed) return;
      project.cursor = page.next_cursor;
      project.historyError = null;
      // A restored batch is an upload of its own, even when this tab declared its selection as several.
      for (const summary of page.items) {
        if (this.#records.has(summary.batch_id)) continue;
        const record = this.#addRecord({ batchId: summary.batch_id, uploadId: summary.batch_id, projectId,
          createdAt: summary.created_at, inputCount: summary.input_count, live: false, declaration: null, settings: null });
        this.#uploads.set(record.uploadId, [record]);
      }
      this.#touchProject(projectId);
      const selected = project.selected ?? page.items[0]?.batch_id;
      if (selected !== undefined && cursor === undefined) this.select(projectId, selected);
    } catch (error) {
      project.loaded = false;
      project.historyError = getUploadErrorMessage(error);
      this.#touchProject(projectId);
    }
  }

  /**
   * One aggregate receipt read per batch at a time. A request that overlaps a
   * read resolves after a follow-up read that started after the request.
   */
  #poll(record: BatchRecord): Promise<void> {
    // The server holds no receipt for a batch it has not accepted.
    if (record.undeclared !== null) return Promise.resolve();
    if (record.reading !== null) {
      record.pollAgain = true;
      return record.reading.then(() => record.reading ?? undefined);
    }
    clearTimeout(record.pollTimer);
    record.pollTimer = undefined;
    const reading = this.#read(record).finally(() => {
      record.reading = null;
      if (!record.pollAgain) {
        this.#scheduleNextPoll(record);
        return;
      }
      record.pollAgain = false;
      void this.#poll(record);
    });
    record.reading = reading;
    return reading;
  }

  async #read(record: BatchRecord): Promise<void> {
    const readStarted = this.#sequence;
    try {
      const receipt = await apiClient.getUploadBatch(record.projectId, record.batchId);
      if (this.#disposed) return;
      this.#applyReceipt(record, receipt, readStarted);
      record.error = null;
    } catch (error) {
      record.error = `Could not refresh the receipt. ${getUploadErrorMessage(error)}`;
    }
    record.loading = false;
    this.#touch(record);
  }

  #applyReceipt(record: BatchRecord, receipt: UploadBatchReceipt, readStarted: number): void {
    record.receipt = new Map(receipt.inputs.map(input => [input.declaration.input_id, withOmittedEntries(record, input)]));
    record.declaration ??= receipt.declaration;
    for (const [inputId, { settled, sequence }] of record.settlements) {
      const input = record.receipt.get(inputId);
      // A read that started later covers the settlement once it reports the same session,
      // or, for transfers that never finalized, any session past receiving.
      const covered = settled.kind === 'sent'
        ? input?.session_id === settled.session.session_id
        : input?.session_status != null && input.session_status !== 'receiving';
      if (sequence <= readStarted && covered) record.settlements.delete(inputId);
    }
    const project = this.#project(record.projectId);
    project.preferences ??= { tags: receipt.declaration.tags, folderPath: receipt.declaration.folder_path ?? null };
    if (record.live) this.#announceAdmissions(record, record.receipt.values(), receipt.declaration.tsa_enabled);
    void this.#readOmitted(record, receipt.declaration.tsa_enabled);
  }

  /**
   * The aggregate receipt carries every input's entries except archives beyond
   * its size bound. Read those one at a time, once each, after they settle.
   */
  async #readOmitted(record: BatchRecord, tsaEnabled: boolean): Promise<void> {
    const next = nextOmittedRead(record);
    if (record.readingOmitted || next === undefined) return;
    record.readingOmitted = true;
    try {
      const { batch_input: read } = await apiClient.getUploadBatchInput(record.projectId, record.batchId, next.inputId);
      if (this.#disposed || record.receipt === null) return;
      record.omitted.set(next.inputId, { sessionId: next.sessionId, entries: read.entries });
      record.receipt = new Map([...record.receipt].map(([inputId, input]) => [inputId, withOmittedEntries(record, input)]));
      if (record.live) this.#announceAdmissions(record, [read], tsaEnabled);
      this.#touch(record);
    } catch (error) {
      record.error = `Could not load an archive's documents. ${getUploadErrorMessage(error)}`;
      this.#touch(record);
      return;
    } finally {
      record.readingOmitted = false;
    }
    void this.#readOmitted(record, tsaEnabled);
  }

  #announceAdmissions(record: BatchRecord, inputs: Iterable<UploadBatchInput>, tsaEnabled: boolean): void {
    const admitted: string[] = [];
    for (const input of inputs) {
      for (const entry of input.entries) {
        if (entry.outcome === 'admitted' && entry.document_id != null && !record.announced.has(entry.document_id)) admitted.push(entry.document_id);
      }
    }
    if (admitted.length === 0) return;
    for (const documentId of admitted) record.announced.add(documentId);
    const event: DocumentsAdmitted = { projectId: record.projectId, batchId: record.uploadId, documentIds: admitted, tsaEnabled };
    for (const listener of this.#admissionListeners) listener(event);
  }

  #scheduleNextPoll(record: BatchRecord): void {
    const active = declaredInputs(record).some(input => isVerifying(record, input.input_id));
    if (!active || this.#disposed || document.hidden) return;
    record.pollTimer = setTimeout(() => { void this.#poll(record); }, record.pollDelay);
    record.pollDelay = Math.min(POLL_MAX_MS, Math.round(record.pollDelay * 1.5));
  }

  #pollSoon(record: BatchRecord): void {
    record.pollDelay = POLL_INITIAL_MS;
    if (record.reading !== null) {
      record.pollAgain = true;
      return;
    }
    if (record.pollTimer !== undefined || document.hidden) return;
    record.pollTimer = setTimeout(() => { void this.#poll(record); }, POLL_SOON_MS);
  }

  #onVisibility = (): void => {
    if (document.hidden) return;
    for (const record of this.#records.values()) {
      if (record.pollTimer === undefined && record.reading === null && record.receipt !== null) this.#scheduleNextPoll(record);
    }
  };

  #touch(record: BatchRecord): void {
    this.#dirtyBatches.add(record.batchId);
    this.#schedulePublish();
  }

  #touchProject(projectId: string): void {
    this.#dirtyProjects.add(projectId);
    this.#schedulePublish();
  }

  #schedulePublish(): void {
    if (this.#disposed) return;
    this.#publishTimer ??= setTimeout(this.#publish, PUBLISH_INTERVAL_MS);
  }

  #publish = (): void => {
    this.#publishTimer = undefined;
    for (const batchId of this.#dirtyBatches) {
      const record = this.#records.get(batchId);
      if (record === undefined) continue;
      this.#rebuild(record);
      this.#dirtyProjects.add(record.projectId);
    }
    this.#dirtyBatches.clear();
    if (this.#dirtyProjects.size === 0) return;
    for (const projectId of this.#dirtyProjects) this.#rebuildProject(projectId);
    this.#dirtyProjects.clear();
    this.#rebuildActivity();
    for (const listener of this.#listeners) listener();
  };

  /** Rebuild one batch view, reusing every item whose sources did not change. */
  #rebuild(record: BatchRecord): void {
    const items: UploadItem[] = [];
    let summary = EMPTY_SUMMARY;
    for (const input of declaredInputs(record)) {
      const item = this.#item(record, input);
      items.push(item);
      summary = addToSummary(summary, item);
    }
    record.view = {
      ...record.view, items, summary, loading: record.loading, error: record.error,
      tsaEnabled: record.declaration?.tsa_enabled ?? false,
      isSending: record.declaring || summary.sending > 0,
    };
  }

  #item(record: BatchRecord, input: UploadInputDeclaration): UploadItem {
    const id = input.input_id;
    const transfer = record.declaring ? QUEUED : this.#transfers.get(id);
    const settlement = record.settlements.get(id);
    const receipt = record.receipt?.get(id);
    const resendable = transfer === undefined && record.sources.has(id);
    const resent = record.resent.has(id);
    const cached = record.items.get(id);
    if (cached !== undefined && cached.transfer === transfer && cached.settlement === settlement && cached.receipt === receipt
      && cached.resendable === resendable && cached.resent === resent) {
      return cached.item;
    }
    const item: UploadItem = {
      inputId: id, filename: input.filename, path: input.relative_path ?? input.filename,
      sizeBytes: input.size_bytes, isArchive: isArchiveFile({ name: input.filename, type: input.content_type ?? '' }),
      status: deriveInputStatus({ transfer, settled: settlement?.settled, receipt, resendable, resent }),
      entries: receipt?.entries ?? [],
    };
    record.items.set(id, { transfer, settlement, receipt, resendable, resent, item });
    return item;
  }

  /** An upload's view: its only batch's, or all of its batches combined. */
  #uploadView(uploadId: string, [first, ...rest]: readonly BatchRecord[]): UploadBatchView | undefined {
    if (first === undefined || rest.length === 0) return first?.view;
    const parts = [first.view, ...rest.map(record => record.view)];
    const cached = this.#combined.get(uploadId);
    if (cached?.parts.length === parts.length && cached.parts.every((part, index) => part === parts[index])) return cached.view;
    const view = combineViews(first.view, parts);
    this.#combined.set(uploadId, { parts, view });
    return view;
  }

  #rebuildProject(projectId: string): void {
    const project = this.#project(projectId);
    const batches: UploadBatchView[] = [];
    for (const [uploadId, records] of this.#uploads) {
      const view = this.#uploadView(uploadId, records);
      if (view?.projectId === projectId) batches.push(view);
    }
    batches.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const selected = batches.find(batch => batch.batchId === project.selected) ?? batches[0];
    project.view = {
      batches, selectedBatchId: selected?.batchId ?? null, hasOlder: project.cursor !== null,
      historyError: project.historyError, preferences: project.preferences ?? {},
    };
  }

  #rebuildActivity(): void {
    const live: UploadBatchView[] = [];
    for (const [uploadId, records] of this.#uploads) {
      const view = records[0]?.live === true ? this.#uploadView(uploadId, records) : undefined;
      if (view !== undefined) live.push(view);
    }
    const current = live.find(batch => batch.isSending)
      ?? live.sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    this.#activity = {
      sending: live.some(batch => batch.isSending),
      current: current === undefined ? null : { batchId: current.batchId, projectId: current.projectId, isSending: current.isSending },
    };
  }
}

const QUEUED: TransferProgress = { started: false, percentage: 0 };

function addToSummary(summary: UploadBatchSummary, item: UploadItem): UploadBatchSummary {
  const { phase, progress, retryable, needsAttention } = item.status;
  const onServer = phase === 'verifying' || phase === 'received' || phase === 'rejected';
  return {
    received: summary.received + (onServer ? 1 : 0),
    sending: summary.sending + (phase === 'queued' || phase === 'sending' ? 1 : 0),
    retryable: summary.retryable + (retryable ? 1 : 0),
    needsAttention: summary.needsAttention + (needsAttention ? 1 : 0),
    bytesTotal: summary.bytesTotal + item.sizeBytes,
    bytesSent: summary.bytesSent + item.sizeBytes * progress / 100,
  };
}
